// TEMPORARY MOCK ADAPTER for Patient Management (PRD 01).
// In-memory only — state lives in this module's `patients` array and resets on hard reload.
// Mutations resolve with ~300 ms simulated latency. Errors mirror the BE envelope
// described in docs/api-contracts/patient-management.md §1.6–§1.7 (canonical codes,
// `fields.<path>` keyed maps for inline messages, top-level message for toasts).
// Backend switch: VITE_USE_MOCK_API=false flips to the real HTTP adapter (patient.adapter.ts).

import { medicalStaffMockApi } from '@/features/medical-staff/api/medical-staff.mock';
import {
  diseaseCodeMockApi,
  findMockDiseaseCodeGroupId,
} from '@/features/disease-code/api/disease-code.mock';

import {
  PatientApiError,
  type ExportJobResult,
  type ExportPatientsParams,
  type ListPatientsParams,
  type ListPatientsResult,
  type Patient,
  type PatientApi,
  type PatientCloseReason,
  type PatientCreateResult,
  type PatientGender,
  type PatientSortKey,
  type PatientStatus,
  type PatientStatusFilter,
  type RegisterPatientInput,
  type UpdatePatientInput,
  type SortDirection,
} from '../types/patient.types';
import { normalizePhone } from '@/shared/utils/phone';

const LATENCY_MS = 300;
// Canonical (PRD 21 §3.3.1): exactly 11 digits, '010'-prefixed (tested on digits-only value).
const PHONE_REGEX = /^010\d{8}$/;
// SHCT-170 — shared name rule (Admin / Medical Staff mgmt): 2–50 chars.
const NAME_MIN = 2;
const NAME_MAX = 50;
// SHCT-170 — MRN: 1–16 chars, alphanumeric + '-' only; duplicate check is case-sensitive (exact match).
const MRN_REGEX = /^[A-Za-z0-9-]{1,16}$/;
// SHCT-170 — prescription drug / medication cycle: free text, single-line, ≤64 chars.
const TEXT_MAX_LEN = 64;
const MANAGEMENT_PERIOD_MONTHS = 6;
const ENDING_SOON_DAYS = 30;
const INSTITUTION = '울산대학교병원';

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ── Seed ──────────────────────────────────────────────────────────────
// Ported from prototype/js/mock-data.js MockData.patients. Wire normalization:
//   gender '남'/'여' → 'M'/'F'; YYYY.MM.DD → YYYY-MM-DD; registeredAt → ISO
//   datetime; pid='P-{id}'; height/weight/residence derived from prototype
//   formulas (seedIfMissing in patients.html). memo + lastPro start null.

// SHCT-915 — 거주지 is now the road-name address EXACTLY as the patient picked it in the app, not the
// 시/구/동 form admin-api used to shorten it to. The seed carries full addresses so the mock panel shows
// what the real one does.
const ROAD_ADDRESSES = [
  '울산광역시 중구 태화로 123', '울산광역시 중구 우정1길 45', '울산광역시 중구 성남동 200',
  '울산광역시 남구 삼산로 123', '울산광역시 남구 달동로 67', '울산광역시 남구 왕생로 8',
  '울산광역시 동구 방어진순환도로 900', '울산광역시 동구 일산해안길 12', '울산광역시 동구 전하로 55',
  '서울특별시 강남구 테헤란로 152', '부산광역시 해운대구 센텀중앙로 90', '울산광역시 북구 강동로 77',
  '울산광역시 울주군 온산읍 온산로 300', '울산광역시 울주군 언양읍 헌양길 21', '경기도 성남시 분당구 판교로 235',
];

// 상세 주소 is optional, so the seed deliberately leaves some blank — the panel's `-` empty state is part
// of what the customer asked to be able to see (SHCT-915 수용 기준).
const RESIDENCE_DETAILS = [
  '101동 503호', '', '2층', '행복아파트 3동 1201호', '', '', 'B동 4층 401호', '',
];

interface SeedRow {
  id: number;
  name: string;
  gender: PatientGender;
  birthDate: string;        // YYYY-MM-DD
  phone: string;
  email: string | null;
  regNo: string;
  registeredAt: string;     // ISO datetime
  dischargeDate: string | null;  // YYYY-MM-DD or null
  diseaseCode: string;
  diseaseName: string;
  status: PatientStatus;
  closeReason?: PatientCloseReason;
  closedAt?: string;        // YYYY-MM-DD
}

const SEED_ROWS: readonly SeedRow[] = [
  { id: 50, name: '김서연', gender: 'F', birthDate: '1975-05-19', phone: '01042433718', email: 'kim.sy@example.com', regNo: '2026050', registeredAt: '2024-01-19T00:00:00.000Z', dischargeDate: '2026-03-01', diseaseCode: 'F32', diseaseName: '우울 에피소드', status: 'ACTIVE' },
  { id: 49, name: '이준호', gender: 'M', birthDate: '1988-03-22', phone: '01055128834', email: 'lee.jh@example.com', regNo: '2026049', registeredAt: '2024-01-20T00:00:00.000Z', dischargeDate: '2026-02-15', diseaseCode: 'F41', diseaseName: '기타 불안장애', status: 'ACTIVE' },
  { id: 48, name: '박지민', gender: 'F', birthDate: '1990-11-08', phone: '01077234491', email: 'park.jm@example.com', regNo: '2026048', registeredAt: '2024-01-22T00:00:00.000Z', dischargeDate: '2026-01-20', diseaseCode: 'F43', diseaseName: '스트레스-연관 장애', status: 'WITHDRAWN' },
  { id: 47, name: '최영수', gender: 'M', birthDate: '1965-07-14', phone: '01033816620', email: 'choi.ys@example.com', regNo: '2026047', registeredAt: '2024-01-25T00:00:00.000Z', dischargeDate: '2026-01-25', diseaseCode: 'F00', diseaseName: '알츠하이머병에서의 치매', status: 'ACTIVE' },
  { id: 46, name: '정미래', gender: 'F', birthDate: '1995-02-28', phone: '01099452217', email: 'jung.mr@example.com', regNo: '2026046', registeredAt: '2024-01-28T00:00:00.000Z', dischargeDate: '2025-12-10', diseaseCode: 'F41', diseaseName: '기타 불안장애', status: 'ACTIVE' },
  { id: 45, name: '한동욱', gender: 'M', birthDate: '1978-09-03', phone: '01066348812', email: 'han.dw@example.com', regNo: '2026045', registeredAt: '2024-02-01T00:00:00.000Z', dischargeDate: '2026-03-10', diseaseCode: 'F10', diseaseName: '알코올 사용에 의한 장애', status: 'ACTIVE' },
  { id: 44, name: '윤소희', gender: 'F', birthDate: '1992-06-17', phone: '01022785543', email: 'yoon.sh@example.com', regNo: '2026044', registeredAt: '2024-02-05T00:00:00.000Z', dischargeDate: '2026-02-05', diseaseCode: 'F32', diseaseName: '우울 에피소드', status: 'ACTIVE' },
  { id: 43, name: '김태현', gender: 'M', birthDate: '1970-12-25', phone: '01088913367', email: 'kim.th@example.com', regNo: '2026043', registeredAt: '2024-02-08T00:00:00.000Z', dischargeDate: '2025-11-05', diseaseCode: 'F25', diseaseName: '조현정동장애', status: 'ACTIVE' },
  { id: 42, name: '오은정', gender: 'F', birthDate: '1983-04-11', phone: '01033459921', email: 'oh.ej@example.com', regNo: '2026042', registeredAt: '2024-02-12T00:00:00.000Z', dischargeDate: '2026-02-10', diseaseCode: 'F43', diseaseName: '스트레스-연관 장애', status: 'ACTIVE' },
  { id: 41, name: '서민재', gender: 'M', birthDate: '1991-08-30', phone: '01066784432', email: 'seo.mj@example.com', regNo: '2026041', registeredAt: '2024-02-15T00:00:00.000Z', dischargeDate: '2025-10-20', diseaseCode: 'F14', diseaseName: '코카인 사용에 의한 장애', status: 'WITHDRAWN' },
  { id: 40, name: '강하늘', gender: 'F', birthDate: '1997-01-05', phone: '01011227788', email: 'kang.hn@example.com', regNo: '2026040', registeredAt: '2024-02-20T00:00:00.000Z', dischargeDate: '2026-01-15', diseaseCode: 'F32', diseaseName: '우울 에피소드', status: 'ACTIVE' },
  { id: 39, name: '임정우', gender: 'M', birthDate: '1972-11-22', phone: '01044553312', email: 'lim.jw@example.com', regNo: '2026039', registeredAt: '2024-02-25T00:00:00.000Z', dischargeDate: '2026-02-25', diseaseCode: 'F00', diseaseName: '알츠하이머병에서의 치매', status: 'ACTIVE' },
  { id: 38, name: '조아란', gender: 'F', birthDate: '1986-07-08', phone: '01088991123', email: 'cho.ar@example.com', regNo: '2026038', registeredAt: '2024-03-01T00:00:00.000Z', dischargeDate: '2025-09-30', diseaseCode: 'F31', diseaseName: '양극성 정동 장애', status: 'CLOSED', closeReason: 'COMPLETED', closedAt: '2026-03-30' },
  { id: 37, name: '황재민', gender: 'M', birthDate: '1969-03-16', phone: '01022335567', email: 'hwang.jm@example.com', regNo: '2026037', registeredAt: '2024-03-05T00:00:00.000Z', dischargeDate: '2026-03-05', diseaseCode: 'F43', diseaseName: '스트레스-연관 장애', status: 'ACTIVE' },
  { id: 36, name: '이다은', gender: 'F', birthDate: '1994-12-01', phone: '01099114456', email: 'lee.de@example.com', regNo: '2026036', registeredAt: '2024-03-10T00:00:00.000Z', dischargeDate: '2025-08-25', diseaseCode: 'F41', diseaseName: '기타 불안장애', status: 'ACTIVE' },
  { id: 35, name: '배상호', gender: 'M', birthDate: '1980-06-25', phone: '01033447789', email: 'bae.sh@example.com', regNo: '2026035', registeredAt: '2024-03-15T00:00:00.000Z', dischargeDate: '2025-07-15', diseaseCode: 'F10', diseaseName: '알코올 사용에 의한 장애', status: 'WITHDRAWN' },
  { id: 34, name: '신혜원', gender: 'F', birthDate: '1989-09-14', phone: '01055662234', email: 'shin.hw@example.com', regNo: '2026034', registeredAt: '2024-03-20T00:00:00.000Z', dischargeDate: '2026-03-20', diseaseCode: 'F32', diseaseName: '우울 에피소드', status: 'ACTIVE' },
  { id: 33, name: '류경민', gender: 'M', birthDate: '1976-02-18', phone: '01077889901', email: 'ryu.km@example.com', regNo: '2026033', registeredAt: '2024-03-25T00:00:00.000Z', dischargeDate: '2025-06-10', diseaseCode: 'F25', diseaseName: '조현정동장애', status: 'ACTIVE' },
  { id: 32, name: '문지영', gender: 'F', birthDate: '1968-10-07', phone: '01011993345', email: 'moon.jy@example.com', regNo: '2026032', registeredAt: '2024-04-01T00:00:00.000Z', dischargeDate: '2026-04-01', diseaseCode: 'F32', diseaseName: '우울 에피소드', status: 'ACTIVE' },
  { id: 31, name: '차승우', gender: 'M', birthDate: '1993-05-29', phone: '01044776623', email: 'cha.sw@example.com', regNo: '2026031', registeredAt: '2024-04-05T00:00:00.000Z', dischargeDate: '2025-05-20', diseaseCode: 'F20', diseaseName: '조현병', status: 'CLOSED', closeReason: 'REHOSPITALIZED', closedAt: '2025-11-20' },
  { id: 30, name: '송예진', gender: 'F', birthDate: '1985-08-12', phone: '01088221144', email: 'song.yj@example.com', regNo: '2026030', registeredAt: '2024-04-10T00:00:00.000Z', dischargeDate: '2026-04-10', diseaseCode: 'F41', diseaseName: '기타 불안장애', status: 'ACTIVE' },
  { id: 29, name: '양현우', gender: 'M', birthDate: '1974-04-03', phone: '01066558837', email: 'yang.hw@example.com', regNo: '2026029', registeredAt: '2024-04-15T00:00:00.000Z', dischargeDate: '2025-04-01', diseaseCode: 'F43', diseaseName: '스트레스-연관 장애', status: 'ACTIVE' },
];

function expandSeed(row: SeedRow): Patient {
  const isFemale = row.gender === 'F';
  const baseH = isFemale ? 160 : 173;
  const baseW = isFemale ? 55 : 72;
  // Match prototype seedIfMissing — deterministic by id, 1-decimal kg.
  const height = baseH + ((row.id % 10) - 5);
  const weight = Math.round((baseW + ((row.id % 15) - 7) * 0.9) * 10) / 10;
  return {
    id: row.id,
    pid: `P-${row.id}`,
    name: row.name,
    gender: row.gender,
    birthDate: row.birthDate,
    phone: row.phone,
    email: row.email,
    height,
    weight,
    residence: ROAD_ADDRESSES[row.id % ROAD_ADDRESSES.length] ?? null,
    residenceDetail: RESIDENCE_DETAILS[row.id % RESIDENCE_DETAILS.length] || null,
    diseaseCode: row.diseaseCode,
    diseaseName: row.diseaseName,
    institution: INSTITUTION,
    regNo: row.regNo,
    dischargeDate: row.dischargeDate,
    registeredAt: row.registeredAt,
    lastPro: null,
    status: row.status,
    closeReason: row.closeReason ?? null,
    closedAt: row.closedAt ?? null,
    // SHCT-981 — every seed patient carries the same two-person team (ids from the medical-staff mock), so
    // the 담당 의료진 row renders in mock mode as it does against the real server.
    assignedStaff: [
      { id: 50, name: '지의사', primary: true },
      { id: 49, name: '박간호', primary: false },
    ],
  };
}

let patients: Patient[] = SEED_ROWS.map(expandSeed);
let nextId = Math.max(...patients.map((p) => p.id)) + 1;

// SHCT-170 — Guardian 1:1 account policy. The real BE checks the Guardian App Accounts table for a
// duplicate guardian mobile (GUARDIAN_MOBILE_DUPLICATE). The mock simulates that uniqueness constraint
// against this in-memory set of normalized (digits-only) guardian phones. Seeded with one value so QC
// can trigger the duplicate path; new guardian-mode registrations append here.
const guardianPhones = new Set<string>(['01099998888']);

// ── Helpers ───────────────────────────────────────────────────────────

function findById(id: number): Patient {
  const target = patients.find((p) => p.id === id);
  if (!target) {
    throw new PatientApiError('NOT_FOUND', '해당 환자를 찾을 수 없습니다.');
  }
  return target;
}

function todayMidnight(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function todayISODate(): string {
  const t = todayMidnight();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
}

function daysUntilExpiry(p: Patient, today: Date): number | null {
  if (p.status !== 'ACTIVE' || !p.dischargeDate) return null;
  const exp = new Date(p.dischargeDate);
  if (Number.isNaN(exp.getTime())) return null;
  exp.setMonth(exp.getMonth() + MANAGEMENT_PERIOD_MONTHS);
  return Math.floor((exp.getTime() - today.getTime()) / 86_400_000);
}

function isEndingSoon(p: Patient, today: Date): boolean {
  const d = daysUntilExpiry(p, today);
  return d !== null && d >= 0 && d <= ENDING_SOON_DAYS;
}

// PRD §4.1: missing dischargeDate ("미정") sinks regardless of dir, like
// medical-staff's null-registeredAt behavior. Other keys are straight asc/desc.
function comparePatient(
  a: Patient,
  b: Patient,
  key: PatientSortKey,
  dir: SortDirection,
): number {
  const sign = dir === 'asc' ? 1 : -1;
  switch (key) {
    case 'id':
      return (a.id - b.id) * sign;
    case 'name':
      return a.name.localeCompare(b.name) * sign;
    case 'gender':
      return (a.gender ?? '').localeCompare(b.gender ?? '') * sign;
    case 'birthDate':
      return a.birthDate.localeCompare(b.birthDate) * sign;
    case 'dischargeDate': {
      const va = a.dischargeDate;
      const vb = b.dischargeDate;
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      return va.localeCompare(vb) * sign;
    }
    case 'registeredAt': {
      // SHCT-794 — a patient with no app account has no 회원가입일, and this is the DEFAULT sort key:
      // if nulls floated to the top on asc, flipping the default would fill the first page with rows
      // that have no date. So they sink whichever way the column is sorted — the same rule
      // medical-staff already applies to its own registeredAt, and this list to 퇴원일 above.
      const va = a.registeredAt;
      const vb = b.registeredAt;
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      return va.localeCompare(vb) * sign;
    }
  }
}

interface FilterParams {
  q?: string;
  status?: Exclude<PatientStatusFilter, 'all'>;
  sortBy: PatientSortKey;
  sortDir: SortDirection;
}

function filterAndSort(params: FilterParams): Patient[] {
  const today = todayMidnight();
  let filtered = [...patients];
  if (params.q) {
    const needle = params.q.trim().toLowerCase();
    if (needle) {
      // PRD §3.2: partial match across name / phone / regNo, case-insensitive.
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(needle) ||
          (p.phone ?? '').toLowerCase().includes(needle) ||
          p.regNo.toLowerCase().includes(needle),
      );
    }
  }
  if (params.status) {
    filtered = filtered.filter((p) => {
      switch (params.status) {
        case 'ACTIVE':
          // Contract §2.2: `ACTIVE` includes ending-soon rows (any expiry distance).
          return p.status === 'ACTIVE';
        case 'ending':
          return p.status === 'ACTIVE' && isEndingSoon(p, today);
        case 'CLOSED':
          return p.status === 'CLOSED';
        case 'WITHDRAWN':
          return p.status === 'WITHDRAWN';
        default:
          return true;
      }
    });
  }
  filtered.sort((a, b) => comparePatient(a, b, params.sortBy, params.sortDir));
  return filtered;
}

// ── Validation ────────────────────────────────────────────────────────
// All codes + messages match docs/api-contracts/patient-management.md §1.7.
// `fields.<path>` keys also match the React modal's FieldErrors map so inline
// messages light up the right input on the first failure.

function trim(s: string | null | undefined): string {
  return (s ?? '').trim();
}

function validateRegister(input: RegisterPatientInput): void {
  if (input.appUserType !== 'patient' && input.appUserType !== 'guardian') {
    throw new PatientApiError('APP_USER_TYPE_INVALID', '앱 사용자 유형을 선택해주세요.', {
      fields: { appUserType: '앱 사용자 유형을 선택해주세요.' },
    });
  }
  const name = trim(input.name);
  if (name.length < NAME_MIN || name.length > NAME_MAX) {
    throw new PatientApiError('NAME_INVALID', '이름은 2~50자로 입력해주세요.', {
      fields: { name: '이름은 2~50자로 입력해주세요.' },
    });
  }
  if (!trim(input.birthDate)) {
    throw new PatientApiError('BIRTHDATE_REQUIRED', '생년월일을 입력해주세요.', {
      fields: { birthDate: '생년월일을 입력해주세요.' },
    });
  }
  if (trim(input.birthDate) > todayISODate()) {
    throw new PatientApiError('PATIENT_DOB_FUTURE_INVALID', '생년월일은 오늘 이전이어야 합니다.', {
      fields: { birthDate: '생년월일은 오늘 이전이어야 합니다.' },
    });
  }
  if (!input.gender) {
    throw new PatientApiError('GENDER_REQUIRED', '성별을 선택해주세요.', {
      fields: { gender: '성별을 선택해주세요.' },
    });
  }
  const phoneMsg = '올바른 휴대폰 번호를 입력해주세요. (예: 010-1234-5678)';
  if (!PHONE_REGEX.test(normalizePhone(input.phone) ?? '')) {
    throw new PatientApiError('PHONE_INVALID', phoneMsg, { fields: { phone: phoneMsg } });
  }
  const mrn = trim(input.mrn);
  if (!mrn) {
    throw new PatientApiError('MRN_REQUIRED', '등록번호(MRN)를 입력해주세요.', {
      fields: { mrn: '등록번호(MRN)를 입력해주세요.' },
    });
  }
  if (!MRN_REGEX.test(mrn)) {
    const mrnMsg = "등록번호(MRN)는 1~16자의 영문·숫자·'-'만 입력할 수 있습니다.";
    throw new PatientApiError('PATIENT_MRN_INVALID', mrnMsg, { fields: { mrn: mrnMsg } });
  }
  // Case-sensitive exact-match duplicate check (SHCT-170 Q2).
  if (patients.some((row) => row.regNo === mrn)) {
    throw new PatientApiError('PATIENT_DUPLICATE_MRN', '이미 등록된 환자입니다.', {
      fields: { mrn: '이미 등록된 환자입니다.' },
    });
  }
  if (!trim(input.dischargeDate)) {
    throw new PatientApiError('DISCHARGE_DATE_REQUIRED', '퇴원일을 입력해주세요.', {
      fields: { dischargeDate: '퇴원일을 입력해주세요.' },
    });
  }
  if (trim(input.dischargeDate) > todayISODate()) {
    throw new PatientApiError('DISCHARGE_DATE_FUTURE', '퇴원일은 오늘 이후로 설정할 수 없습니다.', {
      fields: { dischargeDate: '퇴원일은 오늘 이후로 설정할 수 없습니다.' },
    });
  }
  if (input.mainDiagnosisGroupId == null) {
    throw new PatientApiError('PRIMARY_DIAGNOSIS_REQUIRED', '주진단을 선택해주세요.', {
      fields: { mainDiagnosisGroupId: '주진단을 선택해주세요.' },
    });
  }
  // 부진단(질병코드) optional, but when present it must belong to the chosen 주진단(질병군) — mirrors the BE
  // domain-dependency check (PatientRegistrationService). The dependent dropdown normally prevents a mismatch,
  // but the mock enforces it too so mock/HTTP error behavior matches (parity).
  if (input.subDiagnosisCodeId != null) {
    const codeGroupId = findMockDiseaseCodeGroupId(input.subDiagnosisCodeId);
    if (codeGroupId === undefined) {
      throw new PatientApiError('SECONDARY_DIAGNOSIS_INVALID', '등록되지 않은 부진단입니다.', {
        fields: { subDiagnosisCodeId: '등록되지 않은 부진단입니다.' },
      });
    }
    if (codeGroupId !== input.mainDiagnosisGroupId) {
      throw new PatientApiError(
        'SECONDARY_DIAGNOSIS_GROUP_MISMATCH',
        '부진단은 선택한 주진단(질병군)에 속한 질병코드만 선택할 수 있습니다.',
        { fields: { subDiagnosisCodeId: '부진단은 선택한 주진단(질병군)에 속한 질병코드만 선택할 수 있습니다.' } },
      );
    }
  }
  // SHCT-170 — prescription drug / medication cycle: ≤64 chars (no charset/format restriction).
  if (trim(input.prescriptionDrug).length > TEXT_MAX_LEN) {
    const msg = '처방약은 64자 이하로 입력해주세요.';
    throw new PatientApiError('PATIENT_MEDICATION_INVALID', msg, { fields: { medication: msg } });
  }
  if (trim(input.medicationCycle).length > TEXT_MAX_LEN) {
    const msg = '복약 주기는 64자 이하로 입력해주세요.';
    throw new PatientApiError('PATIENT_MEDICATION_INVALID', msg, {
      fields: { medicationFrequency: msg },
    });
  }
  if (input.appUserType === 'guardian') {
    // Field-error keys use the modal's dotted FieldErrors paths ('guardian.*') so inline messages light up.
    const guardianName = trim(input.guardianName);
    if (!guardianName) {
      throw new PatientApiError('GUARDIAN_NAME_REQUIRED', '보호자 이름을 입력해주세요.', {
        fields: { 'guardian.name': '보호자 이름을 입력해주세요.' },
      });
    }
    if (guardianName.length < NAME_MIN || guardianName.length > NAME_MAX) {
      const msg = '보호자 이름은 2~50자로 입력해주세요.';
      throw new PatientApiError('NAME_INVALID', msg, { fields: { 'guardian.name': msg } });
    }
    if (!input.guardianRelationship) {
      throw new PatientApiError('GUARDIAN_RELATIONSHIP_REQUIRED', '환자와의 관계를 선택해주세요.', {
        fields: { 'guardian.relationship': '환자와의 관계를 선택해주세요.' },
      });
    }
    const guardianDigits = normalizePhone(input.guardianPhone);
    if (!PHONE_REGEX.test(guardianDigits ?? '')) {
      throw new PatientApiError('GUARDIAN_PHONE_INVALID', '올바른 보호자 휴대폰 번호를 입력해주세요.', {
        fields: { 'guardian.phone': '올바른 보호자 휴대폰 번호를 입력해주세요.' },
      });
    }
    // SHCT-170 Q4 — Guardian 1:1 policy: reject a mobile already used by an existing guardian account.
    if (guardianDigits && guardianPhones.has(guardianDigits)) {
      const msg =
        '이미 보호자 계정으로 사용 중인 번호입니다. 동일 보호자가 여러 환자를 맡는 경우 연구간호사에게 문의해주세요.';
      throw new PatientApiError('GUARDIAN_MOBILE_DUPLICATE', msg, {
        fields: { 'guardian.phone': msg },
      });
    }
  }
}

/** Render the same RFC-4180 CSV the backend streams (mock parity), Korean headers, raw values. */
function buildCsv(rows: Patient[]): string {
  const genderKo = (g: PatientGender) => (g === 'M' ? '남' : '녀');
  const statusKo: Record<PatientStatus, string> = {
    ACTIVE: '관리중',
    CLOSED: '관리종료',
    WITHDRAWN: '탈퇴',
  };
  const cell = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const header = ['UID', '이름', '성별', '생년월일', '전화번호', '퇴원일', '회원가입일']
    .map(cell)
    .join(',');
  const body = rows
    .map((p) =>
      [
        p.pid,
        p.name,
        p.gender ? genderKo(p.gender) : '-',
        p.birthDate,
        p.phone,
        p.dischargeDate ?? '-',
        p.registeredAt?.slice(0, 10) ?? '',
      ]
        .map((v) => cell(String(v)))
        .join(','),
    )
    .join('\r\n');
  void statusKo;
  return `﻿${header}\r\n${body}\r\n`;
}

// The server's `statusLabel` on a 409 PATIENT_DUPLICATE_MRN is the display label, not the enum.
const MOCK_STATUS_LABELS: Record<Patient['status'], string> = {
  ACTIVE: '관리중',
  CLOSED: '관리종료',
  WITHDRAWN: '탈퇴',
};

function maskNameMock(name: string): string {
  const n = name.trim();
  if (n.length <= 1) return n;
  if (n.length === 2) return `${n[0]}*`;
  return `${n[0]}${'*'.repeat(n.length - 2)}${n[n.length - 1]}`;
}

// ── API ───────────────────────────────────────────────────────────────

export const patientMockApi: PatientApi = {
  async listPatients(params: ListPatientsParams): Promise<ListPatientsResult> {
    await delay();
    const page = Math.max(1, params.page);
    const perPage = Math.max(1, params.perPage);
    const filtered = filterAndSort(params);
    const total = filtered.length;
    const start = (page - 1) * perPage;
    const items = filtered.slice(start, start + perPage);
    return clone({ items, total, page, perPage });
  },

  async exportPatients(params: ExportPatientsParams): Promise<Blob> {
    await delay();
    return new Blob([buildCsv(filterAndSort(params))], { type: 'text/csv;charset=utf-8' });
  },

  // Async large-file export (mock): the real BE queues a job + emails a link; the mock just acknowledges
  // with a fake QUEUED job id so the success toast ("we'll email you") fires identically against either backend.
  async requestExportJob(_params: ExportPatientsParams): Promise<ExportJobResult> {
    await delay();
    return { id: nextId++, status: 'QUEUED' };
  },

  async getPatient(id: number): Promise<Patient> {
    await delay();
    return clone(findById(id));
  },

  async registerPatient(input: RegisterPatientInput): Promise<PatientCreateResult> {
    await delay();
    validateRegister(input);

    const id = nextId++;
    const created: Patient = {
      id,
      pid: `P-${id}`,
      name: input.name.trim(),
      gender: input.gender,
      birthDate: input.birthDate,
      phone: input.phone.trim(),
      // email / height / weight / residence / lastPro populated later during app sign-up.
      email: null,
      height: input.height ?? null,
      weight: input.weight ?? null,
      residence: input.district ?? null,
      // 상세 주소 has no registration field — it is entered in the app, or corrected on the panel.
      residenceDetail: null,
      // The list/detail render diagnosis from the joined masters; the mock keeps the code slots null.
      diseaseCode: null,
      diseaseName: null,
      institution: INSTITUTION,
      regNo: input.mrn.trim(),
      dischargeDate: input.dischargeDate,
      registeredAt: new Date().toISOString(),
      lastPro: null,
      status: 'ACTIVE',
      closeReason: null,
      closedAt: null,
    };
    patients = [created, ...patients];
    // SHCT-170 Q4 — record the guardian mobile so a later registration with the same number is rejected.
    if (input.appUserType === 'guardian') {
      const guardianDigits = normalizePhone(input.guardianPhone);
      if (guardianDigits) guardianPhones.add(guardianDigits);
    }
    return {
      id,
      uid: `U-${id}`,
      mrn: created.regNo,
      maskedName: maskNameMock(created.name),
      status: 'ACTIVE',
      // The create result's createdAt is the record-creation instant, which always exists — unlike
      // `registeredAt`, now nullable because 회원가입일 is the app-signup date (SHCT-794). The mock
      // seeds them from the same clock, so read it back defensively rather than asserting non-null.
      createdAt: created.registeredAt ?? new Date().toISOString(),
      // smsSent is left off deliberately — the real 201 does not carry it either (BE
      // FLAG_DEBT_PATIENT_SMS_FAILURE_SURFACING_01), and a mock that reported success the server
      // cannot vouch for would hide exactly the gap this field exists to expose.
    };
  },

  // FEATURE_015 — correct a patient (PRD 02 v1.37 §3.6 전체 편집 모드). Mirrors the backend contract the
  // panel relies on: an absent key leaves the stored value alone; guardian fields are refused for a 본인-mode
  // patient (GUARDIAN_FIELD_NOT_APPLICABLE) and a duplicate MRN with the same 409 meta the server sends.
  async updatePatient(id: number, input: UpdatePatientInput): Promise<void> {
    await delay();
    const patient = findById(id);

    const sendsGuardian =
      input.guardianName !== undefined ||
      input.guardianRelationship !== undefined ||
      input.guardianPhone !== undefined;
    if (sendsGuardian && patient.appUserType !== 'guardian') {
      throw new PatientApiError(
        'GUARDIAN_FIELD_NOT_APPLICABLE',
        '본인 모드 환자에는 보호자 정보를 저장할 수 없습니다.',
      );
    }
    if (input.mrn !== undefined) {
      const holder = patients.find((p) => p.id !== id && p.regNo === input.mrn);
      if (holder) {
        throw new PatientApiError('PATIENT_DUPLICATE_MRN', '이미 등록된 환자입니다.', {
          meta: {
            uid: `U-${holder.id}`,
            maskedName: maskNameMock(holder.name),
            status: holder.status.toLowerCase(),
            statusLabel: MOCK_STATUS_LABELS[holder.status],
          },
        });
      }
    }

    if (input.name !== undefined) patient.name = input.name;
    if (input.gender !== undefined) patient.gender = input.gender;
    if (input.birthDate !== undefined) patient.birthDate = input.birthDate;
    if (input.phone !== undefined) patient.phone = input.phone || null;
    if (input.mrn !== undefined) patient.regNo = input.mrn;
    if (input.residence !== undefined) patient.residence = input.residence || null;
    if (input.residenceDetail !== undefined) {
      patient.residenceDetail = input.residenceDetail || null;
    }
    if (input.guardianPhone !== undefined) patient.guardianPhone = input.guardianPhone;
    // 담당 의료진 — names are resolved from the medical-staff mock the picker lists from, the way the server
    // joins them; an id that is in neither keeps the null name a deleted account has.
    if (input.assignedStaffIds !== undefined) {
      const known = new Map((patient.assignedStaff ?? []).map((s) => [s.id, s.name]));
      const directory = await medicalStaffMockApi.listMedicalStaff({
        page: 1,
        perPage: 500,
        sortBy: 'name',
        sortDir: 'asc',
      });
      for (const s of directory.items) known.set(s.id, s.name);
      patient.assignedStaff = input.assignedStaffIds.map((staffId, i) => ({
        id: staffId,
        name: known.get(staffId) ?? null,
        primary: i === 0,
      }));
    }
    if (input.dischargeDate !== undefined) patient.dischargeDate = input.dischargeDate;
    if (input.prescriptionDrug !== undefined) patient.prescriptionDrug = input.prescriptionDrug;
    if (input.medicationCycle !== undefined) patient.medicationCycle = input.medicationCycle;
    if (input.guardianName !== undefined) patient.guardianName = input.guardianName;
    if (input.guardianRelationship !== undefined) {
      patient.guardianRelationship = input.guardianRelationship;
    }

    // The panel shows diagnosis by NAME, so the mock resolves the ids the way the server does — otherwise a
    // saved 주진단 would leave the old name on screen and read as a failed save.
    if (input.mainDiagnosisGroupId !== undefined) {
      const groups = await diseaseCodeMockApi.listGroups();
      patient.mainDiagnosisName =
        groups.find((g) => g.id === input.mainDiagnosisGroupId)?.name ?? null;
    }
    // SHCT-737 QC ② — the removal is its own instruction: an omitted field and a null one arrive
    // identically at the server, so the code field cannot carry "remove it". 부진단 is optional
    // (PRD 02 §3.7), so that instruction has to exist. The code is ignored when the flag is set.
    if (input.clearSubDiagnosis) {
      patient.diseaseCode = null;
      patient.diseaseName = null;
    } else if (input.subDiagnosisCodeId !== undefined) {
      const groupId =
        findMockDiseaseCodeGroupId(input.subDiagnosisCodeId)
        ?? input.mainDiagnosisGroupId;
      const codes = groupId == null ? [] : await diseaseCodeMockApi.listCodes(groupId);
      const picked = codes.find((c) => c.id === input.subDiagnosisCodeId);
      patient.diseaseCode = picked?.code ?? '';
      patient.diseaseName = picked?.name ?? '';
    }
    // SHCT-737 QC ④ / SHCT-738 — the list replaces what is stored, in the order it arrived.
    if (input.medications !== undefined) {
      patient.medications = input.medications.map((m) => ({ ...m }));
    }
  },

  // [US-PM-07] Re-send the app-install SMS. There is no SMS gateway behind the mock, so this resolves;
  // the failure branch is exercised against the real backend's 409 PATIENT_SMS_SEND_FAILED.
  async resendInstallSms(id: number): Promise<void> {
    await delay();
    findById(id);
  },
};
